import { useState } from 'react'
import { ChatProps } from '@dataTypes/message.types'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import {
  bulkUpdateConversation,
  deleteConversation,
  updateCallsById,
  updateChat,
  updateSelectedMessage,
} from '@store/actions'
import {
  getPhoneCallerConversationId,
  getPhoneCallerMessageId,
} from '@utils/chat.util'
import { useNotificationToast } from '@context/NotificationToastContext'
import {
  createSuccessToast,
  createFailedToast,
} from '@utils/notificationToast.util'
import { setCurrentCall, setHasActiveCall } from '@store/reducers/calls.reducer'
import { SET_SHOW_MODAL } from '@store/reducers/messageCenter.reducer'

function useChat(): ChatProps {
  const { showToast } = useNotificationToast()
  const conversations = useAppSelector((state) => state.chat.data)
  const selectedMessage = useAppSelector((state) => state.chat.selectedMessage)
  const showModal = useAppSelector(
    (state) => state.messageCenter.show.deleteChat,
  )
  const chatList = useAppSelector((state) => state.messageCenter.chatList)
  const dispatch = useAppDispatch()
  const device = useAppSelector((state) => state.calls.device)
  const currentCall = useAppSelector((state) => state.calls.currentCall)
  const callerInfo = useAppSelector((state) => state.calls.callerInfo)
  const hasActiveCall = useAppSelector((state) => state.calls.hasActiveCall)
  const [isMuted, setIsMuted] = useState<boolean>(false)
  const [showStartChat, setShowStartChat] = useState<boolean>(false)

  const handleAccept = (): void => {
    if (callerInfo && callerInfo.call_id) {
      currentCall?.accept()
      dispatch(setHasActiveCall(true))
      dispatch(
        updateCallsById({
          call_id: callerInfo.call_id,
          updates: {
            call_status: 'In Progress',
          },
        }),
      )

      if (conversations) {
        const messageId =
          getPhoneCallerMessageId(
            conversations,
            callerInfo,
            '[CHAT-CALL-LOG]::{isCalling}',
          ) ||
          getPhoneCallerMessageId(
            conversations,
            callerInfo,
            '[CHAT-CALL-LOG]::{isRinging}',
          )
        if (messageId) {
          dispatch(
            updateChat({
              chat_message_id: messageId,
              data: { content: '[CHAT-CALL-LOG]::{isInProgressCall}' },
            }),
          )
        }
      }
    }
  }

  const handleDecline = (sendAMessage?: boolean): void => {
    currentCall?.reject()
    dispatch(setHasActiveCall(false))
    dispatch(setCurrentCall(null))

    if (conversations && callerInfo) {
      const messageId =
        getPhoneCallerMessageId(
          conversations,
          callerInfo,
          '[CHAT-CALL-LOG]::{isCalling}',
        ) ||
        getPhoneCallerMessageId(
          conversations,
          callerInfo,
          '[CHAT-CALL-LOG]::{isRinging}',
        )
      if (messageId) {
        dispatch(
          updateChat({
            chat_message_id: messageId,
            data: { content: '[CHAT-CALL-LOG]::{isMissedCall}' },
          }),
        )
      }
    }

    if (callerInfo && sendAMessage && conversations) {
      console.info('Send a message')
      const convId = getPhoneCallerConversationId(conversations, callerInfo)
      if (convId && callerInfo.contact && callerInfo.contact.cellphone) {
        dispatch(
          updateSelectedMessage({
            id: convId,
          }),
        )
      }
    }
  }

  const handleEndCall = async (): Promise<void> => {
    if (callerInfo) {
      console.info('Call disconnected!')
      currentCall?.disconnect()
      dispatch(setHasActiveCall(false))
      dispatch(setCurrentCall(null))
    }
  }

  const handleMic = (): void => {
    console.info('Handling mic button')
    if (currentCall) {
      setIsMuted(!isMuted)
      currentCall.mute(!isMuted)
      console.info(`Mic is ${currentCall.isMuted() ? 'muted' : 'unmuted'}`)
    }
  }

  const onDeleteModalHide = (): void => {
    dispatch(SET_SHOW_MODAL({ deleteChat: false }))
  }

  const onConfirmDeleteChat = async (): Promise<void> => {
    if (chatList.length > 0) {
      if (chatList.length === 1) {
        try {
          await dispatch(deleteConversation(chatList[0])).unwrap()
          showToast(
            createSuccessToast(
              'Chat deleted',
              'Conversation has been deleted.',
            ),
          )
          dispatch(updateSelectedMessage())
        } catch {
          showToast(
            createFailedToast(
              'Delete failed',
              'Deleting chat failed. Please try again.',
            ),
          )
        }
      } else {
        dispatch(
          bulkUpdateConversation({
            ids: chatList,
            data: { is_hidden: true },
          }),
        )
      }
    }
    onDeleteModalHide()
  }

  return {
    callerInfo,
    currentCall,
    device,
    handleAccept,
    handleDecline,
    handleEndCall,
    handleMic,
    hasActiveCall,
    isMuted,
    onConfirmDeleteChat,
    onDeleteModalHide,
    selectedMessage,
    setShowStartChat,
    showStartChat,
    showModal,
  }
}

export default useChat
